import React, { useState, useEffect } from 'react';
import { MapPin, CheckCircle, XCircle, Clock, Navigation, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { formatDate } from '../../utils/dateTimeUtils';

const StaffAttendanceMark = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [attendanceMarked, setAttendanceMarked] = useState(false);
  const [attendanceData, setAttendanceData] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [location, setLocation] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    checkTodayAttendance();
  }, []);

  const checkTodayAttendance = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/staff/self-attendance/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.marked) {
        setAttendanceMarked(true);
        setAttendanceData(response.data);
      }
    } catch (error) {
      console.error('Error checking attendance:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const markAttendance = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (!navigator.geolocation) {
      setMessage({
        type: 'error',
        text: 'Geolocation is not supported by your browser'
      });
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;
        
        setLocation({ latitude, longitude });

        try {
          const response = await axios.post(
            `${API_URL}/api/staff/self-attendance/mark`,
            { latitude, longitude },
            { headers: { Authorization: `Bearer ${token}` } }
          );

          setMessage({
            type: 'success',
            text: response.data.message
          });
          setAttendanceMarked(true);
          setAttendanceData({
            status: response.data.status,
            check_in_time: response.data.check_in_time,
            marked_by: 'self'
          });
        } catch (error) {
          console.error('Error marking attendance:', error);
          setMessage({
            type: 'error',
            text: error.response?.data?.detail || 'Failed to mark attendance'
          });
        } finally {
          setLoading(false);
        }
      },
      (error) => {
        let errorMessage = 'Unable to get your location';
        if (error.code === error.PERMISSION_DENIED) {
          errorMessage = '❌ Location permission denied. Please enable location access in your browser settings.';
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMessage = '❌ Location information unavailable. Please check your GPS settings.';
        } else if (error.code === error.TIMEOUT) {
          errorMessage = '❌ Location request timed out. Please try again.';
        }
        
        setMessage({ type: 'error', text: errorMessage });
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  if (checkingStatus) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking today's attendance...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Mark Attendance</h1>
        <p className="text-sm text-gray-500 mt-1">
          Mark your daily attendance using GPS location
        </p>
      </div>

      {/* Staff Info Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-2xl font-bold text-blue-600">
              {user?.full_name?.charAt(0) || 'S'}
            </span>
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{user?.full_name || 'Staff Member'}</h2>
            <p className="text-sm text-gray-600">{user?.email}</p>
            <p className="text-sm text-gray-500 mt-1">
              {formatDate()}
            </p>
          </div>
        </div>
      </div>

      {/* Attendance Status */}
      {attendanceMarked ? (
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="text-green-600" size={32} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-green-800">Attendance Marked!</h3>
              <p className="text-sm text-green-700 mt-1">
                You have successfully marked your attendance for today
              </p>
              <div className="mt-3 flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-green-600" />
                  <span className="font-medium">Check-in: {attendanceData?.check_in_time}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-green-200 text-green-800 rounded text-xs font-medium">
                    {attendanceData?.status}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Message Display */}
          {message.text && (
            <div className={`px-4 py-3 rounded-lg flex items-start gap-3 ${
              message.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-700' 
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              )}
              <p className="text-sm whitespace-pre-line">{message.text}</p>
            </div>
          )}

          {/* Mark Attendance Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <MapPin className="text-blue-600" size={48} />
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Ready to Mark Attendance?</h3>
            <p className="text-gray-600 mb-6">
              Click the button below to capture your location and mark attendance
            </p>

            <button
              onClick={markAttendance}
              disabled={loading}
              className="inline-flex items-center gap-3 px-8 py-4 bg-blue-600 text-white text-lg font-semibold rounded-lg hover:bg-blue-700 disabled:bg-blue-300 transition-all transform hover:scale-105"
            >
              {loading ? (
                <>
                  <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                  Marking Attendance...
                </>
              ) : (
                <>
                  <Navigation size={24} />
                  Mark Attendance Now
                </>
              )}
            </button>

            {location && (
              <div className="mt-6 text-sm text-gray-500">
                <p>Location captured:</p>
                <p className="font-mono">{location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Instructions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 Instructions</h3>
        <div className="space-y-3 text-sm text-gray-700">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs">
              1
            </div>
            <p>You must be physically present at the campus/office location</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs">
              2
            </div>
            <p>Enable location/GPS on your device before clicking the button</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs">
              3
            </div>
            <p>Allow location permission when your browser asks</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs">
              4
            </div>
            <p>System will verify if you're within the allowed campus radius</p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs">
              5
            </div>
            <p>Attendance can only be marked once per day</p>
          </div>
        </div>
      </div>

      {/* Privacy Note */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-xs text-gray-600">
          🔒 <strong>Privacy Note:</strong> Your location is only used to verify your presence at campus. 
          Location data is stored securely and used only for attendance purposes.
        </p>
      </div>
    </div>
  );
};

export default StaffAttendanceMark;
